import { useEffect, useState } from 'react';

import { canOpenStats, MatchCard, useMatchesStore } from '@/entities/match';
import { OverlayPortal, RADIUS, Skeleton, Squircle } from '@/shared/ui';

import { MatchStatsModal } from './MatchStatsModal';

import styles from './MatchList.module.scss';

/**
 * Лента сыгранных матчей (вкладка «Завершённые») — GET /matches/finished,
 * тот же `MatchCardDto`, что у открытых.
 *
 * Фильтра здесь нет намеренно: что считается сыгранным, решает сервер.
 * Между окончанием матча и показом результата стоит задержка публикации
 * из `game_setting` (ГДД §5.6), и отбор по `status` на клиенте показал бы
 * игроку счёт раньше срока.
 *
 * Тап по карточке открывает статистику (что зашло, что нет) — только если
 * на матч вообще есть свои Прогнозы, иначе показывать нечего и MatchCard
 * сам не кликается.
 */
export function FinishedMatchList() {
  const items = useMatchesStore((state) => state.finished);
  const isLoading = useMatchesStore((state) => state.isLoadingFinished);
  const error = useMatchesStore((state) => state.finishedError);
  const load = useMatchesStore((state) => state.loadFinished);
  const [openMatchId, setOpenMatchId] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, [load]);

  // Симметрично «Текущим»: оверлей во весь экран рисуется только под
  // непустое содержимое
  const found = items.find((match) => match.id === openMatchId) ?? null;
  const openMatch = found && canOpenStats(found) ? found : null;

  if (isLoading && items.length === 0) {
    return <Skeleton count={3} height={140} />;
  }

  if (error) {
    return (
      <p className={styles.error} role="alert" data-testid="finished-matches-error">
        {error}
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <Squircle cornerRadius={RADIUS.xl} className={styles.wrap}>
        <p className={styles.empty} data-testid="finished-matches-empty">
          Сегодня завершённых матчей нет
        </p>
      </Squircle>
    );
  }

  return (
    <>
      <Squircle cornerRadius={RADIUS.xl} className={styles.wrap}>
        <ul className={styles.list} data-testid="finished-match-list">
          {items.map((match) => (
            <li key={match.id}>
              <MatchCard
                match={match}
                onClick={
                  canOpenStats(match)
                    ? () => {
                        setOpenMatchId(match.id);
                      }
                    : undefined
                }
              />
            </li>
          ))}
        </ul>
      </Squircle>

      {openMatch ? (
        <OverlayPortal>
          <div className={styles.overlay}>
            <MatchStatsModal
              match={openMatch}
              onClose={() => {
                setOpenMatchId(null);
              }}
            />
          </div>
        </OverlayPortal>
      ) : null}
    </>
  );
}
